import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { getJwtToken } from "../../Redux/AdminReducer/action";
import { useNavigate } from "react-router-dom";
import { END_POINT } from "../../Redux/AdminReducer/action";
import RightWrapper from "../../Components/CommonComponents/ComponentWrapper";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useGetLoggedInUser } from "../../UtilityHookFunctions/LoggedInUser";
import {
  BsArrowLeft,
  BsBoxArrowUpRight,
  BsClipboard,
  BsCheck,
  BsSearch,
  BsGlobe2,
  BsSliders,
  BsCloudCheck,
  BsExclamationCircle,
  BsLayers,
} from "react-icons/bs";
import { FaSpinner } from "react-icons/fa";
import AutoSectionEditor from "../../Componentsv2/SiteConfigInputs";

const postItem = async (data, path) => {
  try {
    const result = await axios.post(`${END_POINT}/site_config/` + path, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: getJwtToken(),
      },
    });
    toast.success(result?.data?.message || "site updated successfully");
    return true;
  } catch (error) {
    toast.error(error?.response?.data || "Something went wrong");
    console.error(error);
    return false;
  }
};

const fetchItem = async (path) => {
  try {
    const result = await axios.get(
      path ? `${END_POINT}/site_config/${path}` : `${END_POINT}/site_config/`,
      { headers: { Authorization: getJwtToken() } }
    );
    return result.data;
  } catch (error) {
    console.error(error);
  }
};

// "aboutUs" / "about_us" -> "About Us"
const prettyLabel = (key) =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const groupNode = (node) => {
  if (!node || typeof node !== "object" || Array.isArray(node)) return node;

  const out = {};
  Object.entries(node).forEach(([key, value]) => {
    const dot = key.indexOf(".");
    if (dot <= 0) {
      const existing = out[key];
      if (existing && typeof existing === "object" && !Array.isArray(existing) &&
          value && typeof value === "object" && !Array.isArray(value)) {
        Object.entries(value).forEach(([k, v]) => {
          if (existing[k] === undefined) existing[k] = v;
        });
        return;
      }
      if (existing === undefined) out[key] = value;
      return;
    }

    const head = key.slice(0, dot);
    const tail = key.slice(dot + 1);

    if (out[head] === undefined) out[head] = {};
    if (typeof out[head] !== "object" || Array.isArray(out[head])) {
      out[key] = value;
      return;
    }

    out[head][tail] = value;
  });

  Object.keys(out).forEach((k) => {
    out[k] = groupNode(out[k]);
  });

  return out;
};

const groupDottedKeys = (config) => {
  if (!config || typeof config !== "object") return config;
  const grouped = {};
  Object.entries(config).forEach(([page, section]) => {
    grouped[page] = groupNode(section);
  });
  return grouped;
};

const SiteConfig = () => {
  const [pages, setPages] = useState(null);
  const [currentPage, setCurrentPage] = useState();
  const [currentSection, setCurrentSection] = useState();
  const [modifiedData, setModifiedData] = useState({});
  const [site, setsite] = useState({});
  const [siteurl, setsiteurl] = useState();
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sectionQuery, setSectionQuery] = useState("");
  const user = useGetLoggedInUser();
  const iframeRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "IFRAME_READY" && iframeRef.current) {
        iframeRef.current.contentWindow.postMessage(
          { type: "AUTH_DATA", token: "abc123", userId: "42", isAdmin: true },
          "*" // lock later
        );
      }

      if (e.data?.type === "EDITED_DATA_RESPONSE") {
        console.log("Edited data from iframe:", e.data.payload);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  let domainsite = `shiven.shivenclinic.com`;

  const navigate = useNavigate();

  const fetchConfig = async () => {
    const configData = groupDottedKeys(await fetchItem());
    if (configData) {
      setPages(configData);
      const firstPage = Object.keys(configData)[0];
      setCurrentPage(firstPage);
      setCurrentSection(Object.keys(configData[firstPage] || {})[0]);
    } else {
      setPages({});
    }
  };

  useEffect(() => {
    fetchItem("getsiteurl").then((res) => setsiteurl(res));
    fetchConfig();
  }, [site]);

  useEffect(() => {
    fetchItem(`tenant/v2/config/getsite`)
      .then((data) => setsite(data))
      .catch((error) => console.error("Error fetching site data:", error));
  }, []);

  const changeCount = Object.keys(modifiedData).length;

  const saveConfig = async () => {
    if (!changeCount || saving) return;
    setSaving(true);
    const ok = await postItem(modifiedData, "upload-data");
    if (ok) setModifiedData({});
    setSaving(false);
  };

  // Re-pulls the saved config so discarded edits disappear from the form too.
  const discardChanges = async () => {
    if (!changeCount || saving) return;
    const page = currentPage;
    const section = currentSection;
    setModifiedData({});
    const configData = groupDottedKeys(await fetchItem());
    if (configData) {
      setPages(configData);
      if (configData[page]) {
        setCurrentPage(page);
        if (configData[page][section]) setCurrentSection(section);
      }
    }
    toast.info("Changes discarded");
  };

  const copyUrl = async () => {
    if (!siteurl) return;
    await navigator.clipboard.writeText(siteurl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  // Sections of the active page, filtered by the search box.
  const sections = useMemo(() => {
    const all = Object.keys(pages?.[currentPage] || {});
    if (!sectionQuery.trim()) return all;
    const q = sectionQuery.toLowerCase();
    return all.filter((s) => s.toLowerCase().includes(q));
  }, [pages, currentPage, sectionQuery]);

  // A page/section is "dirty" when any pending change path sits under it.
  const isDirty = (prefix) =>
    Object.keys(modifiedData).some((p) => p === prefix || p.startsWith(`${prefix}.`));

  const selectPage = (page) => {
    setCurrentPage(page);
    setSectionQuery("");
    setCurrentSection(Object.keys(pages[page] || {})[0]);
  };

  const hasConfig = Object.keys(pages || {}).length > 0;

  return (
    <RightWrapper>
      <div className="min-h-screen bg-slate-50 -m-3 p-4 md:p-6">
        {/* ---------- Header ---------- */}
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate(-1)}
              title="Go back"
              className="mt-0.5 cursor-pointer rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-100"
            >
              <BsArrowLeft className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-3">
              <div className="hidden h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-600 sm:flex">
                <BsSliders className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                  Site Config
                </h1>
                <p className="mt-0.5 text-xs text-slate-500">
                  Edit the content shown on your public clinic website, page by page.
                </p>
              </div>
            </div>
          </div>

          {hasConfig && (
            <div className="flex flex-wrap items-center gap-2">
              {siteurl && (
                <div className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
                  <BsGlobe2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="max-w-[180px] truncate font-mono text-slate-600 md:max-w-[260px]">
                    {siteurl}
                  </span>
                  <button
                    onClick={copyUrl}
                    title="Copy site link"
                    className="shrink-0 cursor-pointer text-slate-400 transition hover:text-slate-700"
                  >
                    {copied ? (
                      <BsCheck className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <BsClipboard className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              )}

              <button
                onClick={() => window.open(siteurl, "_blank", "noopener,noreferrer")}
                disabled={!siteurl}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              >
                <BsBoxArrowUpRight className="h-3.5 w-3.5" />
                <span>Visit site</span>
              </button>

              <button
                onClick={saveConfig}
                disabled={!changeCount || saving}
                className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none md:text-sm"
              >
                {saving ? (
                  <>
                    <FaSpinner className="h-3.5 w-3.5 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <BsCloudCheck className="h-4 w-4" />
                    {changeCount ? `Save ${changeCount} change${changeCount === 1 ? "" : "s"}` : "Saved"}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* ---------- Body ---------- */}
        {pages === null ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-24 text-slate-500 shadow-sm">
            <FaSpinner className="mb-3 h-7 w-7 animate-spin text-sky-600" />
            <p className="text-sm font-medium">Loading site configuration…</p>
          </div>
        ) : hasConfig ? (
          <>
            {/* Page tabs */}
            <div className="mb-5 flex gap-1.5 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
              {Object.keys(pages).map((page) => {
                const active = page === currentPage;
                return (
                  <button
                    key={page}
                    onClick={() => selectPage(page)}
                    className={`relative shrink-0 cursor-pointer rounded-lg px-3.5 py-2 text-sm font-semibold transition ${
                      active
                        ? "bg-slate-900 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {prettyLabel(page)}
                    <span
                      className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {Object.keys(pages[page] || {}).length}
                    </span>
                    {isDirty(page) && (
                      <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-amber-400" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
              {/* Section rail */}
              <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:sticky lg:top-4">
                <div className="mb-2 flex items-center gap-2 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <BsLayers className="h-3.5 w-3.5" />
                  Sections
                </div>

                <div className="relative mb-2">
                  <BsSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                  <input
                    value={sectionQuery}
                    onChange={(e) => setSectionQuery(e.target.value)}
                    placeholder="Search sections"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-7 pr-2 text-xs text-slate-700 placeholder-slate-400 transition focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                  />
                </div>

                <nav className="max-h-[60vh] space-y-0.5 overflow-y-auto">
                  {sections.length === 0 ? (
                    <p className="px-2 py-4 text-center text-xs text-slate-400">
                      No sections match.
                    </p>
                  ) : (
                    sections.map((section) => {
                      const active = section === currentSection;
                      return (
                        <button
                          key={section}
                          onClick={() => setCurrentSection(section)}
                          className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition ${
                            active
                              ? "bg-sky-50 font-semibold text-sky-700 ring-1 ring-sky-200"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <span className="truncate">{prettyLabel(section)}</span>
                          {isDirty(`${currentPage}.${section}`) && (
                            <span
                              title="Unsaved changes"
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                            />
                          )}
                        </button>
                      );
                    })
                  )}
                </nav>
              </aside>

              {/* Editor */}
              <section className="min-w-0 rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3.5">
                  <div>
                    <h2 className="text-base font-bold text-slate-900">
                      {prettyLabel(currentSection || "")}
                    </h2>
                    <p className="mt-0.5 font-mono text-[11px] text-slate-400">
                      {currentPage}
                      {currentSection ? `.${currentSection}` : ""}
                    </p>
                  </div>

                  {changeCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                      <BsExclamationCircle className="h-3 w-3" />
                      {changeCount} unsaved
                    </span>
                  )}
                </div>

                <div className="p-5">
                  <AutoSectionEditor
                    setModifiedData={setModifiedData}
                    sectionKey={currentSection ? `${currentPage}.${currentSection}` : currentPage}
                    pages={pages}
                    setPages={setPages}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3">
                  <button
                    onClick={discardChanges}
                    disabled={!changeCount || saving}
                    className="cursor-pointer rounded-lg px-3 py-2 text-xs font-medium text-slate-500 transition hover:bg-slate-200/60 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Discard changes
                  </button>
                  <button
                    onClick={saveConfig}
                    disabled={!changeCount || saving}
                    className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                  >
                    {saving ? (
                      <>
                        <FaSpinner className="h-3.5 w-3.5 animate-spin" /> Saving…
                      </>
                    ) : (
                      <>
                        <BsCloudCheck className="h-4 w-4" /> Save changes
                      </>
                    )}
                  </button>
                </div>
              </section>
            </div>
          </>
        ) : user?.role === "MainDoctor" ? (
          <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100 text-sky-600">
              <BsGlobe2 className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Set up your website</h3>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-slate-500">
              Create your clinic's public site to start editing its pages, sections and images
              from here.
            </p>
            <button
              disabled={creating}
              onClick={async () => {
                setCreating(true);
                try {
                  const newSite = { user_id: user?._id, domain: domainsite };
                  await postItem(newSite, "create_site");
                  await fetchConfig();
                } catch (error) {
                  toast.error("Failed to create site");
                } finally {
                  setCreating(false);
                }
              }}
              className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {creating ? (
                <>
                  <FaSpinner className="h-3.5 w-3.5 animate-spin" /> Setting up…
                </>
              ) : (
                "Set up site"
              )}
            </button>
          </div>
        ) : (
          <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600">
              <BsExclamationCircle className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">No site configured yet</h3>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-slate-500">
              Ask the main doctor of this clinic to create the website before content can be
              edited.
            </p>
          </div>
        )}
      </div>

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar newestOnTop />
    </RightWrapper>
  );
};

export default SiteConfig;
